// asiaAIalliance — single source of truth

export const eventInfo = {
  edition: "asiaAIalliance",
  brand: "asiaAIalliance",
  title: "Where AI Transforms Asia",
  subtitle: "Powered by the Ecosystm AI Alliance Community",
  city: "Singapore",
  venue: "Capella Singapore",
  venueAddress: "1 The Knolls, Sentosa Island, 098297",
  mapUrl: "https://maps.app.goo.gl/NreJUt8qsM8nHsJ96",
  tagline: "Independent. Research-led. In the room.",
  status: "Attendance is by invitation only. Register your interest today.",
  description:
    "This leadership forum brings together some of Asia's most prolific leaders from enterprise, government, investment, academia, startups, and the digital economy to advance AI through collaboration and shared action.",
  longDescription:
    "As AI moves into core business and public sector operations, the questions have become more complex \u2013 from infrastructure and governance to investment, regulation, and value creation. Asia AI Alliance brings together the people responsible for answering them.",
  quote: {
    text: "We believe the most impactful conversations are driven by the right people in the room. That's why every discussion is designed for the leaders shaping AI across Asia.",
    author: "Asia AI Alliance Editorial Principle",
  },
};

// 200 total = 100 enterprise CxOs + 100 ecosystem (founders, investors, policymakers, regulators, IHEs)
export const stats = [
  { label: "Senior leaders", value: "200" },
  { label: "Enterprise CxOs", value: "100" },
  { label: "Ecosystem participants", value: "100" },
  { label: "Day, in person", value: "1" },
];

export const beliefs = [
  {
    title: "Signal Over Noise",
    body:
      "Grounded in research, real-world experience, and practical implementation, not just the headlines",
    accent: "#FF243D",
  },
  {
    title: "Cross-Sector by Design",
    body:
      "Examining AI through the lens of technology, policy, investment, regulation, and society rather than in isolation.",
    accent: "#143DE5",
  },
  {
    title: "Conversations That Continue",
    body:
      "Designed to spark lasting collaboration through the Ecosystm AI Alliance Community, not just discussions confined to the Summit.",
    accent: "#22CC74",
  },
];

export const themes = [
  {
    title: "AI Policy, Regulation & Sovereignty",
    summary:
      "The national priorities, policy choices, and enterprise decisions determining Asia's ability to build, control, and use AI.",
    color: "#FF243D",
  },
  {
    title: "Innovation, Capital & Competitiveness",
    summary:
      "Where capital is moving, where value is being created, and who is building competitive advantage.",
    color: "#143DE5",
  },
  {
    title: "The AI Technology Foundation",
    summary:
      "The compute, architecture, data, networks, and security infrastructure required to support capable AI.",
    color: "#22CC74",
  },
  {
    title: "Making AI Work",
    summary:
      "What real AI deployments reveal about changing technology, operating models, and how value gets delivered.",
    color: "#FF243D",
  },
  {
    title: "AI, People & Society",
    summary:
      "AI's impact on work, skills, healthcare, education, inclusion, sustainability, and everyday life.",
    color: "#143DE5",
  },
  {
    title: "What's Next for AI",
    summary:
      "Breakthroughs, risks, opportunities, and strategic choices shaping AI's trajectory in Asia and beyond.",
    color: "#22CC74",
  },
];

export const formats = [
  {
    name: "Talk show",
    style: "Curated, conversational, moderated by Ecosystm",
    body: "On-stage dialogue with rotating senior guests. No presentations.",
  },
  {
    name: "Spotlight",
    style: "High-signal perspectives from senior leaders",
    body: "Framing context, priorities, and direction.",
  },
  {
    name: "Boardroom Sessions",
    style: "Decision-focused exchanges in curated groups",
    body: "Discussions across core AI themes.",
  },
  {
    name: "Leadership Roundtables",
    style: "Small-group forums, facilitated by Ecosystm",
    body: "Focused peer exchange. Anchored by domain voices.",
  },
];

export const whyAttend = [
  {
    title: "200 decision-makers, one day",
    body: "100 enterprise CxOs and 100 ecosystem leaders \u2014 founders, investors, policymakers, regulators and academics.",
    cta: "See the themes",
    href: "/programme",
  },
  {
    title: "Independent and editorially anchored",
    body: "Hosted, designed and moderated by Ecosystm. No vendor narratives, no managed messaging on stage.",
    cta: "Read about the format",
    href: "/programme",
  },
  {
    title: "The editorial spine, plus industry voices",
    body: "Ecosystm anchor analysts are confirmed. Enterprise CxOs, founders, investors and policymakers are announced from June 2026.",
    cta: "See the speakers",
    href: "/speakers",
  },
];

// Featured Ecosystm analyst speakers.
export const speakers = [
  {
    id: "amit-gupta",
    name: "Amit Gupta",
    role: "Group CEO & Founder",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/amit-gupta.png",
    bio: "Amit founded Ecosystm and serves as Group CEO. He has spent more than 25 years advising governments, enterprises and technology providers on digital transformation, data strategy and ecosystem growth across India, ASEAN and ANZ. Amit anchors the asiaAIalliance editorial line-up.",
    featured: true,
  },
  {
    id: "arishta-khanna",
    name: "Arishta Khanna",
    role: "Group Chief Operating Officer",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/arishta-khanna.png",
    bio: "Arishta is Group Chief Operating Officer at Ecosystm, overseeing operations and delivery across the group's research, advisory and community platforms in Asia Pacific.",
  },
  {
    id: "sash-mukherjee",
    name: "Sash Mukherjee",
    role: "VP Industry Insights",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/sash-mukherjee.png",
    bio: "Sash leads Ecosystm's industry insights practice, covering AI, cloud and digital transformation across Asia Pacific with deep work on FSI, healthcare and the public sector.",
  },
  {
    id: "chris-white",
    name: "Chris White",
    role: "VP, Marketing and Communities",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/chris-white.png",
    bio: "Chris is Vice President, Marketing and Communities at Ecosystm, where he champions meaningful conversations and engagement across the technology community in Asia Pacific.",
  },
  {
    id: "tony-phukan",
    name: "Tony Phukan",
    role: "Director, Business Development & Ecosystem Alliance",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/tony-phukan.png",
    bio: "Tony leads business development and ecosystem alliances at Ecosystm, bringing more than two decades of experience driving growth and partner ecosystems across Asia Pacific and Japan.",
  },
  {
    id: "tan-hai-wun",
    name: "Tan Hai Wun",
    role: "Director, Strategic Accounts",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/tan-hai-wun.png",
    bio: "Hai Wun is Director of Strategic Accounts at Ecosystm, working with enterprise leaders on research-led advisory engagements across the region.",
  },
  {
    id: "manish-prakash",
    name: "Manish Prakash",
    role: "Principal Advisor",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/manish-prakash.png",
    bio: "Manish advises enterprise leaders on AI strategy, enterprise architecture and large-scale platform decisions, with particular focus on India and South-East Asia.",
  },
  {
    id: "louise-francis",
    name: "Louise Francis",
    role: "Principal Advisor",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/louise-francis.png",
    bio: "Louise is a Principal Advisor at Ecosystm, working with technology leaders on AI adoption, customer experience strategy and modern data foundations.",
  },
  {
    id: "manish-goenka",
    name: "Manish Goenka",
    role: "Principal Advisor",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/manish-goenka.png",
    bio: "Manish advises on enterprise AI go-to-market, channel and partner ecosystems across Asia Pacific.",
  },
  {
    id: "samarn-takort",
    name: "Samarn Takort",
    role: "Principal Advisor",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/samarn-takort.png",
    bio: "Samarn is a Principal Advisor at Ecosystm focused on South-East Asia, working with enterprises on AI strategy, governance and operating models.",
  },
  {
    id: "achim-granzen",
    name: "Achim Granzen",
    role: "Principal Advisor",
    org: "Ecosystm",
    track: "Analyst",
    image: "/images/speakers/achim-granzen.png",
    bio: "Achim is a Principal Advisor at Ecosystm with deep expertise in AI, intelligent automation and the operating-model implications of foundation models for global enterprises.",
  },
];

// Full agenda + Day 0 welcome dinner
export const agendaDay0 = [
  { time: "18:00 \u2013 22:00", title: "Welcome Dinner", track: "Invitation only", description: "A hosted welcome dinner for senior executives attending the summit. Pre-event networking with key speakers and guest invitees. Capped at 50 pax." },
];

export const agendaDay1 = [
  { time: "08:00 \u2013 09:00", title: "Breakfast Roundtable", track: "Invitation only", description: "A curated 10\u201312-pax breakfast roundtable for invited senior executives." },
  { time: "09:00 \u2013 09:30", title: "Registration and Coffee", track: "All delegates", description: "Open registration and informal networking ahead of the plenary." },
  { time: "09:30 \u2013 11:30", title: "Plenary Session", track: "Plenary", description: "Opening plenary anchored by Ecosystm. Conversational talk-show format with rotating guests." },
  { time: "11:30 \u2013 11:45", title: "Tea Break", track: "Networking", description: "A short break for refreshments and connections in the room." },
  { time: "11:45 \u2013 12:40", title: "Plenary Session", track: "Plenary", description: "Second plenary block. Continued talk-show format covering the day's key themes." },
  { time: "12:45 \u2013 13:45", title: "Lunch and Networking", track: "Lunch + Lunch Roundtable", description: "Open lunch for all delegates, with a parallel lunch roundtable for an invited group." },
  { time: "13:45 \u2013 14:45", title: "Boardroom Breakout 1A / 1B", track: "Breakouts", description: "Two concurrent boardroom sessions on selected themes \u2014 delegates choose their session." },
  { time: "14:45 \u2013 15:00", title: "Transition and Networking Break", track: "Networking", description: "Short break to move between breakouts and reset." },
  { time: "15:00 \u2013 16:00", title: "Boardroom Breakout 2A / 2B", track: "Breakouts", description: "Second set of two concurrent boardrooms on themes including Policy, Investment, Ecosystems, Enterprise AI, Responsible AI and Skilling." },
  { time: "16:05 \u2013 16:20", title: "Closing Session", track: "Plenary", description: "Day-end recap, key takeaways and what comes next from Ecosystm." },
  { time: "16:30 onwards", title: "Networking Drinks and Close", track: "Networking reception", description: "Hosted networking drinks for ~100 senior executives. Conversations from the plenary and breakouts, extended." },
];

export const sponsorTiers = [
  {
    tier: "Diamond",
    label: "Title sponsor",
    spots: 1,
    priceUsd: 80000,
    gradient: "from-[#FF243D] via-[#E01A32] to-[#7a0d1c]",
    perks: [
      "Plenary Speaking Slot",
      "Boardroom Sessions / Breakout",
      "Executive Meeting Lounge with branding",
      "Branding at the event and in communications",
      "Post-event executive summary",
      "6 event entry badges",
    ],
  },
  {
    tier: "Platinum",
    label: "3 spots",
    spots: 3,
    priceUsd: 55000,
    gradient: "from-[#143DE5] via-[#0c2da8] to-[#080f4a]",
    perks: [
      "Plenary Speaking Slot",
      "Executive Meeting Lounge with branding",
      "Branding at the event and in communications",
      "Post-event executive summary",
      "4 event entry badges",
    ],
  },
  {
    tier: "Gold",
    label: "3 spots",
    spots: 3,
    priceUsd: 40000,
    gradient: "from-[#22CC74] via-[#159157] to-[#0a3a23]",
    perks: [
      "Boardroom Sessions / Breakout",
      "Executive Meeting Lounge with branding",
      "Branding at the event and in communications",
      "Post-event executive summary",
      "3 event entry badges",
    ],
  },
  {
    tier: "Silver",
    label: "4 spots",
    spots: 4,
    priceUsd: 20000,
    gradient: "from-[#2a2a2a] via-[#161616] to-[#000000]",
    perks: [
      "Executive Meeting Lounge with branding",
      "Branding at the event and in communications",
      "Post-event executive summary",
      "2 event entry badges",
    ],
  },
];

export const sponsorAddOns = [
  { name: "Welcome Dinner \u2014 Day 0 (50 pax)", spots: 1, priceUsd: 40000, blurb: "Host the pre-event dinner with senior executives and key speakers. 5-minute speech slot, 2 hours.", icon: "Utensils" },
  { name: "Networking Drinks & Reception (100 pax)", spots: 1, priceUsd: 50000, blurb: "Close the day with CXOs, founders, investors and influencers. 5-minute speech, 2\u20133 hours.", icon: "Wine" },
  { name: "Breakfast Roundtable (10\u201312 pax)", spots: 1, priceUsd: 25000, blurb: "Invite-only morning roundtable. 1 hour. 5-minute speech, 2 entry badges.", icon: "Coffee" },
  { name: "Lunch Roundtable (10\u201312 pax)", spots: 1, priceUsd: 25000, blurb: "Invite-only 60-minute lunch roundtable on a topic you choose.", icon: "Soup" },
  { name: "Boardroom Sessions / Breakouts", spots: 2, priceUsd: 30000, blurb: "Target 40\u201350 pax. 12\u201315 pax around the table. Customer-use-case led, facilitated by Ecosystm.", icon: "Presentation" },
  { name: "Ecosystm Leadership Podcast", spots: 8, priceUsd: 15000, blurb: "20-minute podcast recording with a senior Ecosystm analyst on an AI topic of your choice.", icon: "Mic" },
];

export const postEventPackages = [
  { name: "Executive Roundtable (8\u201310 pax)", standardUsd: 25000, discountedUsd: 20000, blurb: "Curated post-event session with senior executives." },
  { name: "Whitepaper", standardUsd: 15000, discountedUsd: 12000, blurb: "Ecosystm-authored research whitepaper on your chosen theme." },
  { name: "Ecosystm Leadership Podcast", standardUsd: 15000, discountedUsd: 10000, blurb: "Long-form leadership podcast hosted by an Ecosystm analyst." },
  { name: "Ecosystm Byte / eBook", standardUsd: 10000, discountedUsd: 7500, blurb: "Snackable insight piece distributed across Ecosystm channels." },
];

export const sponsors = sponsorTiers.map((t) => ({
  tier: `${t.tier} sponsor`,
  name: `${t.spots} ${t.spots === 1 ? "spot" : "spots"} \u00b7 USD ${(t.priceUsd / 1000).toFixed(0)}k`,
  blurb: t.perks[0],
  url: "mailto:info@ecosystm.io",
}));

export const nav = [
  { label: "Home", to: "/" },
  { label: "Themes & Format", to: "/programme" },
];

export const ecosystmAbout = {
  headline: "About Ecosystm",
  body:
    "Ecosystm is a leading technology market analyst and advisory firm that enables all stakeholders in the ecosystem to navigate innovation in the digital economy through data, insights and expertise.",
  bullets: [
    "Enterprise leaders engage with Ecosystm for research and peer engagement across Asia Pacific.",
    "10 years of empowering technology providers with actionable insights and strategic guidance.",
    "Network of industry analysts across Asia Pacific driving thought leadership on AI and business themes.",
  ],
};
